/**
 * Canonical "drop N nodes in a tidy grid around the cursor" layout
 * helper. Lifted out of `useCanvasCanvasDropTarget.ts` so every
 * multi-node drop pipeline (OS file drops, entity drops, multi-file
 * place onto canvas / into a section, cross-scope copies) uses the
 * exact same grid math.
 *
 * Algorithm (unchanged from the original inline version):
 *   - cols = ceil(sqrt(N))
 *   - row widths sum each slot's `nodeWidth + GAP` (no leading gap)
 *   - row height = max(slot.nodeHeight) within the row
 *   - grid origin = `dropPosition - (totalGridW/2, totalGridH/2)`
 *
 * All returned positions are snapped to `CANVAS_SNAP.grid` so they
 * land on the same grid that React Flow uses for drag snap — preventing
 * misalignment the first time a placed node is moved.
 *
 * The helper is generic over the slot shape so callers can pass any
 * `{ nodeWidth, nodeHeight, ... }` object and re-zip the returned
 * positions with their original metadata.
 */
import {
  CANVAS_SNAP,
  CANVAS_SPACING,
  snapToGrid,
} from "../canvas-controls.config";

export function layoutGridAroundCursor<
  T extends { nodeWidth: number; nodeHeight: number },
>(
  slots: T[],
  dropPosition: { x: number; y: number },
): Array<{ x: number; y: number }> {
  if (slots.length === 0) return [];

  const GAP = CANVAS_SPACING.nodeGap;
  const [snapX, snapY] = CANVAS_SNAP.grid;
  const COLS = Math.ceil(Math.sqrt(slots.length));
  const totalRows = Math.ceil(slots.length / COLS);

  const rowWidths: number[] = new Array(totalRows).fill(0);
  const rowHeights: number[] = new Array(totalRows).fill(0);
  for (let i = 0; i < slots.length; i++) {
    const r = Math.floor(i / COLS);
    rowWidths[r] += slots[i].nodeWidth + (i % COLS === 0 ? 0 : GAP);
    rowHeights[r] = Math.max(rowHeights[r], slots[i].nodeHeight);
  }
  const totalGridW = rowWidths.length ? Math.max(...rowWidths) : 0;
  const totalGridH =
    rowHeights.reduce((sum, h) => sum + h, 0) +
    Math.max(0, totalRows - 1) * GAP;

  // Snap the grid origin so every cell offset (multiples of nodeWidth + GAP)
  // starts from a grid-aligned anchor.
  const originX = snapToGrid(dropPosition.x - totalGridW / 2, snapX);
  const originY = snapToGrid(dropPosition.y - totalGridH / 2, snapY);

  const positions: Array<{ x: number; y: number }> = [];
  let rowTop = originY;
  for (let row = 0; row < totalRows; row++) {
    let x = originX;
    for (let c = 0; c < COLS; c++) {
      const i = row * COLS + c;
      if (i >= slots.length) break;
      positions.push({
        x: snapToGrid(x, snapX),
        y: snapToGrid(rowTop, snapY),
      });
      x += slots[i].nodeWidth + GAP;
    }
    rowTop += rowHeights[row] + GAP;
  }
  return positions;
}
